<script>
import { computed } from 'vue';
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import AutomationActionDmInput from 'dashboard/components/automation/AutomationActionWhatsappDmInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const COMMENT_EVENT_VARIABLES = [
  { key: 'sender_name', label: 'Username de quem comentou' },
  { key: 'message', label: 'Texto do comentário' },
  { key: 'media_type', label: 'Tipo de mídia (post, reel, story)' },
  { key: 'contact_name', label: 'Nome do contato criado' },
];

const COMMENT_ACTION_NAMES = ['send_comment_reply'];

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    AutomationActionDmInput,
    WootMessageEditor,
    NextButton,
    SingleSelect,
    MultiSelect,
    NextInput,
  },
  provide() {
    return {
      commentEventVariables: computed(() =>
        COMMENT_ACTION_NAMES.includes(this.action_name)
          ? COMMENT_EVENT_VARIABLES
          : null
      ),
    };
  },
  props: {
    modelValue: { type: Object, default: () => null },
    actionTypes: { type: Array, default: () => [] },
    dropdownValues: { type: Array, default: () => [] },
    errorMessage: { type: String, default: '' },
    showActionInput: { type: Boolean, default: true },
    initialFileName: { type: String, default: '' },
    isMacro: { type: Boolean, default: false },
    dropdownMaxHeight: { type: String, default: 'max-h-80' },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return (
        this.actionTypes.find(action => action.key === this.action_name)
          ?.inputType ?? 'none'
      );
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return null;
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : null;
      },
      set(value) {
        this.action_name = value?.id || value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(a => ({ id: a.key, name: a.label }));
    },
    isVerticalLayout() {
      return [
        'team_message',
        'textarea',
        'dm_message',
        'quick_reply_message',
        'button_template_message',
      ].includes(this.inputType);
    },
    allInboxes() {
      return this.$store.getters['inboxes/getInboxes'] || [];
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) return this.action_params[0];
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    inputClass() {
      return 'w-full rounded-lg px-3 py-2 text-sm bg-n-alpha-1 outline outline-1 outline-n-weak dark:outline-n-strong focus:outline-2 focus:outline-n-brand';
    },

    // ── quick_reply_message ──
    quickReplyNormalized() {
      const v = this.action_params;
      if (Array.isArray(v)) return v[0] || {};
      if (v && typeof v === 'object') return v;
      return {};
    },
    quickReplyMessage: {
      get() {
        return this.quickReplyNormalized.message || '';
      },
      set(text) {
        this.action_params = { ...this.quickReplyNormalized, message: text };
      },
    },
    quickReplies() {
      return this.quickReplyNormalized.quick_replies || [];
    },

    // ── button_template_message ──
    buttonTemplateNormalized() {
      const v = this.action_params;
      if (Array.isArray(v)) return v[0] || {};
      if (v && typeof v === 'object') return v;
      return {};
    },
    buttonTemplateMessage: {
      get() {
        return this.buttonTemplateNormalized.message || '';
      },
      set(text) {
        this.action_params = {
          ...this.buttonTemplateNormalized,
          message: text,
        };
      },
    },
    buttonTemplateInboxId: {
      get() {
        return this.buttonTemplateNormalized.inbox_id || null;
      },
      set(id) {
        this.action_params = { ...this.buttonTemplateNormalized, inbox_id: id };
      },
    },
    buttons() {
      return this.buttonTemplateNormalized.buttons || [];
    },
    instagramInboxOptions() {
      return (this.$store.getters['inboxes/getInboxes'] || [])
        .filter(i => i.channel_type === 'Channel::Instagram')
        .map(i => ({ id: i.id, name: i.name }));
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },

    // ── quick_reply_message methods ──
    updateQuickReply(index, key, value) {
      const qrs = [...(this.quickReplyNormalized.quick_replies || [])];
      qrs[index] = { ...qrs[index], [key]: value };
      this.action_params = { ...this.quickReplyNormalized, quick_replies: qrs };
    },
    addQuickReply() {
      const qrs = [...(this.quickReplyNormalized.quick_replies || [])];
      if (qrs.length >= 13) return;
      qrs.push({ title: '', payload: '' });
      this.action_params = { ...this.quickReplyNormalized, quick_replies: qrs };
    },
    removeQuickReply(index) {
      const qrs = [...(this.quickReplyNormalized.quick_replies || [])];
      qrs.splice(index, 1);
      this.action_params = { ...this.quickReplyNormalized, quick_replies: qrs };
    },

    // ── button_template_message methods ──
    updateButton(index, key, value) {
      const btns = [...(this.buttonTemplateNormalized.buttons || [])];
      btns[index] = { ...btns[index], [key]: value };
      this.action_params = { ...this.buttonTemplateNormalized, buttons: btns };
    },
    addButton() {
      const btns = [...(this.buttonTemplateNormalized.buttons || [])];
      if (btns.length >= 3) return;
      btns.push({ type: 'postback', title: '', payload: '' });
      this.action_params = { ...this.buttonTemplateNormalized, buttons: btns };
    },
    removeButton(index) {
      const btns = [...(this.buttonTemplateNormalized.buttons || [])];
      btns.splice(index, 1);
      this.action_params = { ...this.buttonTemplateNormalized, buttons: btns };
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <SingleSelect
            v-if="inputType === 'search_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <MultiSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <NextInput
            v-else-if="inputType === 'email'"
            v-model="action_params"
            type="email"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <WootMessageEditor
        v-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        rows="4"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
      <!-- send_dm: seletor de qualquer inbox + textarea -->
      <AutomationActionDmInput
        v-if="inputType === 'dm_message'"
        v-model="action_params"
        :inboxes="allInboxes"
      />

      <!-- send_private_reply_with_quick_reply -->
      <div
        v-if="inputType === 'quick_reply_message'"
        class="flex flex-col gap-2 w-full"
      >
        <textarea
          :value="quickReplyMessage"
          rows="3"
          :placeholder="$t('AUTOMATION.QUICK_REPLY.MESSAGE_PLACEHOLDER')"
          :class="inputClass"
          @input="quickReplyMessage = $event.target.value"
        />
        <div
          v-for="(qr, i) in quickReplies"
          :key="i"
          class="flex items-center gap-2"
        >
          <input
            :value="qr.title"
            maxlength="20"
            :placeholder="$t('AUTOMATION.QUICK_REPLY.TITLE_PLACEHOLDER')"
            :class="inputClass"
            @input="updateQuickReply(i, 'title', $event.target.value)"
          />
          <input
            :value="qr.payload"
            :placeholder="$t('AUTOMATION.QUICK_REPLY.PAYLOAD_PLACEHOLDER')"
            :class="inputClass"
            @input="updateQuickReply(i, 'payload', $event.target.value)"
          />
          <NextButton
            sm
            solid
            slate
            icon="i-lucide-trash"
            class="flex-shrink-0"
            @click="removeQuickReply(i)"
          />
        </div>
        <button
          v-if="quickReplies.length < 13"
          type="button"
          class="self-start text-xs text-n-brand hover:underline"
          @click="addQuickReply"
        >
          {{ $t('AUTOMATION.QUICK_REPLY.ADD_BUTTON') }}
        </button>
      </div>

      <!-- send_button_template -->
      <div
        v-if="inputType === 'button_template_message'"
        class="flex flex-col gap-2 w-full"
      >
        <select
          :value="buttonTemplateInboxId"
          :class="inputClass"
          @change="
            buttonTemplateInboxId = $event.target.value
              ? Number($event.target.value)
              : null
          "
        >
          <option :value="null" disabled>
            {{ $t('AUTOMATION.BUTTON_TEMPLATE.SELECT_INBOX_PLACEHOLDER') }}
          </option>
          <option
            v-for="inbox in instagramInboxOptions"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <textarea
          :value="buttonTemplateMessage"
          rows="3"
          :placeholder="$t('AUTOMATION.BUTTON_TEMPLATE.MESSAGE_PLACEHOLDER')"
          maxlength="640"
          :class="inputClass"
          @input="buttonTemplateMessage = $event.target.value"
        />
        <div
          v-for="(btn, i) in buttons"
          :key="i"
          class="flex flex-col gap-1 rounded-lg p-2 outline outline-1 outline-n-weak dark:outline-n-strong"
        >
          <div class="flex items-center gap-2">
            <select
              :value="btn.type"
              :class="inputClass"
              @change="updateButton(i, 'type', $event.target.value)"
            >
              <option value="postback">
                {{ $t('AUTOMATION.BUTTON_TEMPLATE.TYPE_POSTBACK') }}
              </option>
              <option value="web_url">
                {{ $t('AUTOMATION.BUTTON_TEMPLATE.TYPE_WEB_URL') }}
              </option>
            </select>
            <NextButton
              sm
              solid
              slate
              icon="i-lucide-trash"
              class="flex-shrink-0"
              @click="removeButton(i)"
            />
          </div>
          <input
            :value="btn.title"
            :placeholder="
              $t('AUTOMATION.BUTTON_TEMPLATE.BTN_TITLE_PLACEHOLDER')
            "
            :class="inputClass"
            @input="updateButton(i, 'title', $event.target.value)"
          />
          <input
            v-if="btn.type === 'web_url'"
            :value="btn.url"
            type="url"
            :placeholder="$t('AUTOMATION.BUTTON_TEMPLATE.BTN_URL_PLACEHOLDER')"
            :class="inputClass"
            @input="updateButton(i, 'url', $event.target.value)"
          />
          <input
            v-else
            :value="btn.payload"
            :placeholder="
              $t('AUTOMATION.BUTTON_TEMPLATE.BTN_PAYLOAD_PLACEHOLDER')
            "
            :class="inputClass"
            @input="updateButton(i, 'payload', $event.target.value)"
          />
        </div>
        <button
          v-if="buttons.length < 3"
          type="button"
          class="self-start text-xs text-n-brand hover:underline"
          @click="addButton"
        >
          {{ $t('AUTOMATION.BUTTON_TEMPLATE.ADD_BUTTON') }}
        </button>
      </div>
    </div>
    <span v-if="errorMessage" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>
